import React, { useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { MoreOutlined as MoreVertIcon } from '@ant-design/icons';
import { Button, Dropdown, Pagination, Spin, Table, Typography } from 'antd';
import type { MenuProps } from 'antd';

import clsx from 'clsx';
import Alert from 'components/Alert';
import CalculateCommissionAffiliate from 'components/CalculateCommissionAffiliate';
import { tap } from 'rxjs/operators';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import Truncate from 'v2/components/Shared/TruncateText';
import { EStatusAffiliation } from 'v2/interfaces/affiliation';
import type { IAffiliation } from 'v2/interfaces/affiliation';
import affiliationsService from 'v2/services/affiliations';

import RecruitmentCommission from '../RecruitmentCommission';

export const List = () => {
  const history = useHistory();

  const [isLoading] = useObservable(() => affiliationsService.isLoading(), []);
  const [affiliations] = useObservable(() => affiliationsService.getAffiliations(), []);
  const [pagination] = useObservable(() => affiliationsService.getPaginationResponse(), []);
  const [loadProductById] = useCallbackObservable((id: number) => affiliationsService.loadProductById(id), []);
  const [cancelAffiliation] = useCallbackObservable(
    (affiliation: IAffiliation) =>
      affiliationsService.cancelAffilation(affiliation).pipe(
        tap(() => {
          toast.success('Afiliação cancelada com sucesso');
        }, toast.error)
      ),
    []
  );

  const [isModalRecruitmentOpen, setIsModalRecruitmentOpen] = useState<boolean>(false);

  const onChangeRowsPerPage = (perPage: number) => {
    affiliationsService.setPagination({ ...pagination, per_page: perPage, current_page: 1 });
    document.getElementById('affiliations-table').scrollIntoView();
  };

  const onChangePage = (page: number) => {
    affiliationsService.setPagination({ ...pagination, current_page: page });
  };

  const handleClickShare = (affiliation: IAffiliation) => {
    history.push(`/producer/campaigns/${affiliation.product_id}`);
  };

  const handleRecruitmentClick = (affiliation: IAffiliation) => {
    loadProductById(affiliation.product_id);
    setIsModalRecruitmentOpen(true);
  };

  const closeModalRecruitment = () => {
    setIsModalRecruitmentOpen(false);
  };

  const handleClickCancelAffiliation = async (affiliation: IAffiliation) => {
    const confirmation = await Alert.show({
      title: 'Atenção',
      message: 'Deseja realmente cancelar a afiliação?',
      confirmation: true,
      buttonOk: 'Confirmar',
      buttonCancel: 'Cancelar'
    });

    if (!confirmation) {
      return;
    }

    cancelAffiliation(affiliation);
  };

  const statusAffiliaton = {
    P: 'Pendente',
    A: 'Aprovada',
    R: 'Reprovada'
  };

  const columns = [
    {
      title: 'Status',
      dataIndex: 'affiliations_status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Typography.Text
          strong
          className={clsx(
            'text-xs whitespace-nowrap uppercase',
            status === 'P' ? 'text-[#fabb0a]!' : status === 'A' ? 'text-success!' : 'text-feedback-negative!'
          )}
        >
          {statusAffiliaton[status as keyof typeof statusAffiliaton]}
        </Typography.Text>
      )
    },
    {
      title: 'Produto',
      dataIndex: 'product_title',
      key: 'product',
      width: 300,
      render: (_: string, aff: IAffiliation) => (
        <>
          <Typography.Text strong className='flex items-center text-sm'>
            {aff.product_title}
          </Typography.Text>
          <Typography.Text className='text-sm'>({aff.product_id})</Typography.Text>
          {aff.product_status === 'inactive' && (
            <span className='border-feedback-negative text-feedback-negative mt-2 block w-fit rounded-sm border bg-[#fdecea] px-4 text-[10px] uppercase'>
              Inativo
            </span>
          )}
        </>
      )
    },
    {
      title: 'Produtor',
      key: 'producer',
      width: 200,
      render: (_: any, aff: IAffiliation) => (
        <a href={`/producer/detail/${aff.producer.id}`} target='_blank' rel='noopener noreferrer'>
          <Truncate count={25} final='...'>
            {aff.producer.business_name}
          </Truncate>
        </a>
      )
    },
    {
      title: 'Recrutamento',
      key: 'recruitment',
      width: 120,
      render: (_: any, aff: IAffiliation) => (
        <p className={`uppercase ${aff.recruitment ? 'text-success' : 'opacity-70'}`}>
          {aff.recruitment ? 'Habilitado' : 'Desabilitado'}
        </p>
      )
    },
    { title: 'Recrutados', dataIndex: 'indications', key: 'indications', width: 90, align: 'right' as const },
    {
      title: 'Preço',
      key: 'price',
      width: 140,
      align: 'right' as const,
      render: (_: any, aff: IAffiliation) => (
        <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores' fallback={<em>oculto</em>}>
          {aff.has_variations ? (
            <div>
              <span>máx.: </span>
              <FormattedNumber value={aff.product_max_price} format='BRL' />
            </div>
          ) : (
            <FormattedNumber value={aff.product_price} format='BRL' />
          )}
        </PermissionHide>
      )
    },
    {
      title: 'Comissão',
      key: 'commission',
      width: 250,
      align: 'right' as const,
      render: (_: any, aff: IAffiliation) => (
        <>
          {aff.product_max_comission_charge_type !== 'G' && (
            <>
              <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores' fallback={<em>oculto</em>}>
                {aff.has_variations && !aff.has_differentiated_commission && <>máx: </>}
                <CalculateCommissionAffiliate
                  price={aff.product_price || 0}
                  commissionType={
                    aff.has_differentiated_commission
                      ? aff.differentiated_comission_type
                      : aff.product_max_comission_type
                  }
                  commissionValue={
                    aff.has_differentiated_commission ? aff.differentiated_comission_value : aff.product_max_comission
                  }
                  hasVariations={aff.has_variations}
                />
              </PermissionHide>
              &nbsp;de comissão {aff.product_max_comission_charge_type === 'A' ? 'recorrente' : null}
            </>
          )}
          {aff.product_max_comission_charge_type === 'G' && (
            <>
              <PermissionHide inverse action='myeduzz:meus-produtos:ocultar-valores' fallback={<em>oculto</em>}>
                <CalculateCommissionAffiliate
                  price={aff.product_price ? aff.product_price : 0}
                  commissionType={aff.comission_type}
                  commissionValue={aff.comission}
                />
              </PermissionHide>
              &nbsp;de comissão
            </>
          )}
        </>
      )
    },
    {
      title: '',
      key: 'actions',
      width: 50,
      fixed: 'right' as const,
      align: 'right' as const,
      render: (_: unknown, aff: IAffiliation) => {
        const items: MenuProps['items'] = [];

        if (aff.affiliations_status?.toUpperCase() === EStatusAffiliation.APPROVED) {
          items.push({ key: 'share', label: 'Divulgar', onClick: () => handleClickShare(aff) });

          if (aff.recruitment) {
            items.push({ key: 'recruit', label: 'Recrutar', onClick: () => handleRecruitmentClick(aff) });
          }
        }

        items.push({
          key: 'cancel',
          label: 'Cancelar afiliação',
          onClick: () => handleClickCancelAffiliation(aff)
        });

        return (
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button type='text' icon={<MoreVertIcon />} aria-label='Ações' />
          </Dropdown>
        );
      }
    }
  ];

  return (
    <>
      <Spin spinning={!!isLoading}>
        <Table
          scroll={{ x: 'max-content' }}
          id='affiliations-table'
          dataSource={affiliations || []}
          columns={columns}
          rowKey='id'
          pagination={false}
          loading={isLoading}
        />
        {!isLoading && pagination && (
          <div className='mt-4 flex justify-end'>
            <Pagination
              current={pagination.current_page}
              pageSize={pagination.per_page}
              total={pagination.total_items || 0}
              onChange={onChangePage}
              onShowSizeChange={(_c: number, size: number) => onChangeRowsPerPage(size)}
              showSizeChanger
            />
          </div>
        )}
      </Spin>

      <RecruitmentCommission open={isModalRecruitmentOpen} closeModal={closeModalRecruitment} />
    </>
  );
};
